import {
  AsteriskIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlugZapIcon,
  TrashIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PANEL } from '@/features/workspace-overview/stateStyles';
import { SECTION_HEADING } from '@/shared/components/sectionHeading';
import { EmptyState } from '@/shared/components/EmptyState';

import { COMING_SOON_LABEL } from '../providerTypeOptions';
import type { AiProvider } from '../types';
import { AiProviderTestConnectionButton } from './AiProviderTestConnectionButton';
import { SetDefaultProviderControl } from './SetDefaultProviderControl';

const STATUS_BADGE: Record<
  AiProvider['connection_status'],
  {
    label: string;
    variant: 'secondary' | 'default' | 'destructive';
    className?: string;
  }
> = {
  untested: { label: 'Untested', variant: 'secondary' },
  // `board-state-done` is the brand's success token (a teal, not a raw green —
  // see docs/brand.md's "Success, running" row), given the same light-fill chip
  // treatment the workspace-usage thresholds already use rather than a colour
  // this feature would be inventing on its own.
  ok: {
    label: 'Connected',
    variant: 'secondary',
    className: 'bg-board-state-done/12 text-board-state-done',
  },
  failed: { label: 'Failed', variant: 'destructive' },
};

function AiProviderRow({
  provider,
  onEdit,
  onDelete,
}: {
  provider: AiProvider;
  onEdit: (provider: AiProvider) => void;
  onDelete: (provider: AiProvider) => void;
}) {
  const status = STATUS_BADGE[provider.connection_status];

  return (
    // On a phone, a grid: the mark, the name and the menu on the first line,
    // the status and the test under the name. As one wrapping row the name was
    // clipped to "Capge" and the menu fell onto a line of its own (spec 184).
    <li className="border-border hover:bg-accent focus-within:bg-accent flex flex-wrap items-center gap-3 border-t px-4 py-2.5 transition-colors first:border-t-0 max-sm:grid max-sm:grid-cols-[auto_minmax(0,1fr)_auto]">
      {/* A rounded mark with a many-pointed asterisk — the closest a
          single-colour line-art icon (FD-15) gets to Claude's own sunburst
          mark without reaching for the trademarked logo itself. */}
      <span
        aria-hidden="true"
        className="bg-destructive/12 text-destructive flex size-8 shrink-0 items-center justify-center rounded-full"
      >
        <AsteriskIcon className="size-6" strokeWidth={2.5} />
      </span>

      {/* The name is its own block so `truncate` can end it in an ellipsis; on
          the flex row it clipped without one (spec 184). */}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
          <span className="truncate" title={provider.name}>
            {provider.name}
          </span>
          <SetDefaultProviderControl provider={provider} />
        </span>
        {provider.base_url ? (
          <span className="text-muted-foreground truncate font-mono text-xs">
            {provider.base_url}
          </span>
        ) : null}
      </span>

      {/* `contents` on a wider screen, so these stay items of the row; a
          line of their own under the name on a phone. */}
      <span className="contents max-sm:col-span-2 max-sm:col-start-2 max-sm:row-start-2 max-sm:flex max-sm:flex-wrap max-sm:items-center max-sm:gap-3">
        <Badge variant={status.variant} className={status.className}>
          {status.label}
        </Badge>

        <AiProviderTestConnectionButton
          id={provider.id}
          className="self-center"
        />
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto shrink-0 max-sm:col-start-3 max-sm:row-start-1"
            >
              <MoreHorizontalIcon aria-hidden="true" />
              <span className="sr-only">Actions for {provider.name}</span>
            </Button>
          }
        />
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              onEdit(provider);
            }}
          >
            <PencilIcon aria-hidden="true" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              onDelete(provider);
            }}
          >
            <TrashIcon aria-hidden="true" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

/**
 * The "Claude" group of the AI Providers list — Anthropic providers, which
 * are the only `provider_type` creatable today (spec FR-002).
 */
export function ClaudeProviderGroup({
  providers,
  onEdit,
  onDelete,
  onCreate,
}: {
  providers: AiProvider[];
  onEdit: (provider: AiProvider) => void;
  onDelete: (provider: AiProvider) => void;
  onCreate: () => void;
}) {
  const claudeProviders = providers.filter(
    (provider) => provider.provider_type === 'anthropic',
  );

  return (
    <section className="flex flex-col gap-3" aria-labelledby="claude-heading">
      <div className="flex items-center justify-between">
        <h3 id="claude-heading" className={SECTION_HEADING}>
          Claude
        </h3>
        <Button size="lg" onClick={onCreate}>
          <PlugZapIcon aria-hidden="true" />
          Claude connection
        </Button>
      </div>
      {claudeProviders.length === 0 ? (
        <EmptyState
          icon={PlugZapIcon}
          description="No Claude connections yet."
        />
      ) : (
        <div className={PANEL}>
          <ul className="flex flex-col">
            {claudeProviders.map((provider) => (
              <AiProviderRow
                key={provider.id}
                provider={provider}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * The "Other Providers" group — always empty today, since no other
 * `provider_type` is creatable yet (spec FR-002a).
 */
export function OtherProviderGroup({
  providers,
  onEdit,
  onDelete,
  onCreateOther,
}: {
  providers: AiProvider[];
  onEdit: (provider: AiProvider) => void;
  onDelete: (provider: AiProvider) => void;
  onCreateOther: () => void;
}) {
  const otherProviders = providers.filter(
    (provider) => provider.provider_type !== 'anthropic',
  );

  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="other-providers-heading"
    >
      <div className="flex items-center justify-between">
        <h3 id="other-providers-heading" className={SECTION_HEADING}>
          Other Providers
        </h3>
        <Button size="sm" variant="outline" disabled onClick={onCreateOther}>
          Add provider
        </Button>
      </div>
      {otherProviders.length === 0 ? (
        <EmptyState
          description={`No other providers configured yet. ${COMING_SOON_LABEL}.`}
        />
      ) : (
        <div className={PANEL}>
          <ul className="flex flex-col">
            {otherProviders.map((provider) => (
              <AiProviderRow
                key={provider.id}
                provider={provider}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * The organization's AI Providers, grouped by provider type into a "Claude"
 * group (populated, Anthropic providers) and an "Other Providers" group
 * (always empty today, since no other `provider_type` is creatable yet —
 * spec FR-002/FR-002a). Composes `ClaudeProviderGroup` and
 * `OtherProviderGroup`, which `AiProvidersSection` also renders separately
 * so the Claude group can sit above the LLM Profiles section while Other
 * Providers stays below it.
 */
export function AiProviderList({
  providers,
  onEdit,
  onDelete,
  onCreate,
  onCreateOther,
}: {
  providers: AiProvider[];
  onEdit: (provider: AiProvider) => void;
  onDelete: (provider: AiProvider) => void;
  onCreate: () => void;
  onCreateOther: () => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <ClaudeProviderGroup
        providers={providers}
        onEdit={onEdit}
        onDelete={onDelete}
        onCreate={onCreate}
      />
      <OtherProviderGroup
        providers={providers}
        onEdit={onEdit}
        onDelete={onDelete}
        onCreateOther={onCreateOther}
      />
    </div>
  );
}
